<script setup lang="ts">
    import CloseIcon from '@/components/icons/CloseIcon.ce.vue';
    import ConstellIcon from '@/components/icons/ConstellIcon.ce.vue';
    import { getContrastColor } from '@/helpers/text';

    const props = withDefaults(defineProps<{
        openStateLabel?: string
        closedStateLabel?: string
        brandColor?: string
        isWindowOpen?: boolean

        // Icon-only square; set while a partner button's label is expanded. Never set on
        // center positions (see the collapse gate in App.ce.vue).
        collapsed?: boolean
    }>(), {
        openStateLabel: '',
        closedStateLabel: '',
        brandColor: '#282829',
    });

    const emit = defineEmits<(event: 'letTheWindowGrow') => void>();

    const { t } = useI18n();

    // Computed
    const buttonLabel: ComputedRef<string> = computed(() => props.isWindowOpen
        ? props.openStateLabel || t('widget.buttons.close')
        : props.closedStateLabel || t('widget.buttons.plan-event'));

    // Methods
    const toggleWindow = (): void => {
        emit('letTheWindowGrow');
    };
</script>

<template>
    <button
        class="btn-fab"
        type="button"
        :class="[{ close: isWindowOpen, collapsed }]"
        :style="{ color: getContrastColor(brandColor) }"
        @click="toggleWindow"
    >
        <!-- The span is needed, otherwise styling breaks -->
        <span class="btn-fab-inner">
            <div class="btn-fab-inner__icon">
                <ConstellIcon
                    v-if="!isWindowOpen"
                    :width="12"
                    class="btn-fab-inner__star"
                />
                <CloseIcon
                    v-if="isWindowOpen"
                    :width="24"
                />
            </div>
            <span class="btn-fab-inner__label">{{ buttonLabel }}</span>
        </span>
    </button>
</template>

<style lang="scss" scoped>
.btn-fab {
    border: none;
    border-radius: 5px;
    background: var(--widget-brand-color);
    cursor: pointer;

    // Lets the label's inline-size animate between 0 and auto; unsupported browsers snap.
    interpolate-size: allow-keywords;
    padding-block: var(--spacing-03);
    padding-inline: var(--spacing-03) var(--spacing-04);
    text-align: center;
    text-decoration: none;
    transition: padding-inline-end 0.3s ease;
    white-space: nowrap;

    &.close {
        padding-inline-start: 14px;
    }

    // 24px icon + 16px per side = 56px square, matching the partner buttons' block-size.
    &.collapsed {
        padding-inline-end: var(--spacing-03);
    }

    span {
        color: inherit;
        font: 16px var(--font-primary);
        font-weight: var(--font-weight-regular);
    }
}

.btn-fab-inner {
    display: flex;
    align-items: center;

    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        block-size: var(--size-3);
        color: inherit;
        inline-size: var(--size-3);
    }

    /*
     * Star grows to fill the collapsed square, back to 12px once the label is showing. Scaled
     * rather than sized via the width prop: SVG width/height are attributes, which cannot be
     * transitioned. Scaling also keeps the 56px launcher height fixed - transform does not
     * affect layout, so the star can exceed the 24px icon box without pushing anything.
     */
    &__star {
        // Overshoots well past 1.25 then settles back, so the star pops instead of easing. The
        // curve is deliberately aggressive: the scale delta is only 3px, so a gentler back-out
        // overshoots by a fraction of a pixel and reads as no effect at all.
        transition: transform 0.4s cubic-bezier(0.34, 3.2, 0.64, 1);
    }

    .btn-fab.collapsed &__star {
        transform: scale(1.5);
    }

    /*
     * Keep 0.3s ease in sync with PartnerButtons.__label or the row jitters. No delay here, so
     * the launcher yields width as the partner label takes it. The icon gap is this margin
     * rather than a flex `gap` so it collapses too - a surviving gap leaves the square 8px wide.
     */
    &__label {
        display: inline-block;
        overflow: hidden;
        margin-inline-start: var(--spacing-02);
        transition:
            inline-size 0.3s ease,
            margin-inline-start 0.3s ease;
    }

    .btn-fab.collapsed &__label {
        inline-size: 0;
        margin-inline-start: 0;
        opacity: 0;
    }
}
</style>
